import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

import type { Sample } from '@ctcells/contract'
import { maskPii, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DialogSurface } from '../../atoms/DialogSurface.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Radio } from '../../atoms/Radio.tsx'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import type { gate } from '../access.ts'
import { FormRow } from '../formSections.tsx'
import type { SampleHints } from '../samples/sampleFormatHints.ts'
import { SampleHintInput } from '../samples/SampleHintInput.tsx'
import { sampleTypeHintLabel } from '../samples/sampleFormatHints.ts'
import type { SampleDraft } from '../samples/SampleNewScreen.tsx'
import { patientKey, type WellPicks } from './newBatchAssignments.ts'
import { wellSampleColumns } from './wellSampleColumns.ts'
import { rerunBlocked } from './dispositionModel.ts'

/**
 * 작업 추가 창의 **웰 시료 선택 창** · 페이지 판의 「새 시료」 폼.
 *
 * ⚠ 2026-10-21 ~ 10-26 의 「기존 시료」 팝업(여러 건 골라 담은 차례로 칸을 정함) · 창 안 「새 시료 추가」 팝업은 걷었다
 *   (eric 2026-10-27 「새 시료 추가는 빼」 · 「해당 칸 또는 웰 별로 하는 건 어때?」 → 웰 · 「응 한번 해봐」).
 *   페이지(장비 관점 콘솔)는 종전 그대로 표 아래 펼침이다(`NewSampleForm`) — 그 앱은 이번 범위 밖이다.
 */

type ControlGate = ReturnType<typeof gate>

/**
 * 웰 곁에 뜨는 시료 선택 창 — 그 웰 하나에 시료 하나를 고른다(라디오 · 고르면 닫힌다).
 * 제목은 한 줄 「A2 (P2)」 — 웰 이름 + 한 칸 + 괄호 안 묶음(`sample_ref` — 트레이 칸 이름 P1 · REF · eric 2026-10-29 「A2 (P2) 이렇게 해줘」 ·
 * 10-27 의 「A1웰(P1)」은 걷었다). 검색 칸 아래 안내문(기존 작업 배정 · 시료 처분 · 다시 돌림)은 없다(eric 2026-10-29 「기존 작업에 배정 이런 텍스트 다 빼고」) —
 * 무리 머리(「기존 작업에 배정되어 선택할 수 없는 시료」)와 비활성 사유가 말한다. 묶음 제한 한 줄(`batchNew.well.samePatient`)만 남는다.
 * 목록은 세 구역 — 「이 웰」(지금 든 시료 · 줄 끝 [빼기]) · 「고를 수 있는 시료」 · 「다른 웰에 배정됨」(흐리게 · 꼬리는 웰 이름 하나).
 * 🔴 **다른 웰에 이미 든 시료는 비활성 + 사유**다 — 옮기지 않는다(실수 방지 · 팀장 결정 2026-10-27). 옮기려면 그 웰에서 [빼기] 하고 다시 고른다.
 * 🔴 창(모달) 위의 작은 창이라 Esc 는 «이것만» 닫는다 — `Modal` 의 Esc 는 이미 쓴(`defaultPrevented`) 키를 건너뛴다.
 *    닫으면 부르는 쪽이 누른 웰로 포커스를 돌린다. 바깥을 누르면 닫힌다.
 * 검색 칸은 안내 글자(placeholder) 없이 접근 이름만 둔다(eric 2026-10-27 「환자 번호 검색 플레이스 홀더 지우고」).
 */
export function WellSamplePicker({
  sampleRef,
  wellName,
  samples,
  assignedSamples = [],
  onInspectAssigned,
  picks,
  patientLock,
  emptyLabel,
  view,
  controlGate,
  focusOnOpen = true,
  onPick,
  onClear,
  onClose,
}: {
  /** 그 웰의 묶음 이름(P1 · REF) */
  sampleRef: string
  wellName: string
  /** 고를 수 있는 시료 전부(미배정 · 종전 기존 시료 목록과 같은 조회) */
  samples: readonly Sample[]
  assignedSamples?: readonly Sample[]
  onInspectAssigned?: ((batchId: string) => void) | undefined
  /** 지금 웰마다 고른 것 */
  picks: WellPicks
  /**
   * 같은 묶음의 다른 웰에 든 환자(`patientLockFor`) — 주면 그 환자의 시료만 보인다(eric 2026-10-29 「P1 두 개의 환자 번호가 같아야」).
   * 서버 `GET /samples` 에 환자 거름 파라미터가 없어(`limit` · `offset` · `batch_id` 뿐) 받은 목록에서 거른다. 이 웰에 든 시료는 늘 보인다(뺄 수 있게)
   */
  patientLock?: { key: string; patientId: string | undefined; sampleRef: string } | undefined
  /** 비었을 때 한 줄 — 못 본 까닭(403 · 실패) · 받는 중 · 0건을 부르는 쪽이 가른다 */
  emptyLabel: string
  /** PII 를 볼 수 있는가 — 시료 번호 가림과 검색 대상이 이것을 따른다 */
  view: boolean
  controlGate: ControlGate
  /** 열 때 검색 칸으로 포커스를 옮기나 — 저절로 연 창(레시피를 고른 직후)은 옮기지 않는다 */
  focusOnOpen?: boolean | undefined
  onPick: (sampleId: string) => void
  onClear: () => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  /** 열릴 때 한 번만 읽는다 */
  const focusOnOpenRef = useRef(focusOnOpen)
  useEffect(() => {
    onCloseRef.current = onClose
  })
  /* 열리면 검색 칸으로 · 바깥 누름이면 닫는다(웰을 다시 누른 것도 바깥이다 — 부르는 쪽이 그 웰로 다시 연다) */
  useEffect(() => {
    /* 🔴 굴리지 않고 포커스만 — 자리를 맞추기 전 첫 그림에서 검색 칸으로 본문이 굴러 트레이가 밀렸다(1440 실측) */
    if (focusOnOpenRef.current)
      boxRef.current?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true })
    const onDown = (event: PointerEvent) => {
      const box = boxRef.current
      if (box !== null && event.target instanceof Node && !box.contains(event.target))
        onCloseRef.current()
    }
    /*
     * 🔴 Esc 는 «이 창만» 닫는다 — 문서 잡기 단계(capture)에서 먼저 받아 쓴 키로 표시한다. 그래야 `Modal` 의 Esc(같은 문서 · 거품 단계)가
     *    작업 추가 창까지 닫지 않는다. 저절로 연 창(레시피 고른 직후)은 포커스가 창 밖이라 창 안 `onKeyDown` 만으로는 못 받았다(실측)
     */
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return
      event.preventDefault()
      onCloseRef.current()
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [])
  /*
   * 자리 — 어느 웰을 눌러도 늘 같은 자리: 트레이 오른쪽 배정 표 영역 위에 겹쳐 뜬다(같은 위 끝 · 같은 폭 · 같은 높이 · `newBatch.css` `.nbwell`).
   * eric 2026-10-27 「오른쪽 부분에 동일하게 해줘.. 팝업을 띄우는데」 — 웰마다 창이 따라 움직이던 것을 걷었다
   */

  /* 🔴 가린 값으로는 찾지 않는다 — 못 보는 사람이 검색으로 값을 맞혀 보는 길이 된다 */
  const q = query.trim().toLowerCase()
  const samePatient = (s: Sample) =>
    patientLock === undefined ||
    picks[s.sample_id] === wellName ||
    patientKey(s.patient_id) === patientLock.key
  const scoped = samples.filter(samePatient)
  const rows =
    q === '' || !view
      ? scoped
      : scoped.filter((s) =>
          [s.sample_id, s.patient_id ?? ''].some((v) => v.toLowerCase().includes(q)),
        )
  const current = rows.filter((s) => picks[s.sample_id] === wellName)
  const assigned = assignedSamples.filter(
    (s) =>
      s.batch_id != null &&
      samePatient(s) &&
      (q === '' ||
        !view ||
        [s.sample_id, s.patient_id ?? ''].some((v) => v.toLowerCase().includes(q))),
  )
  const free = rows.filter((s) => picks[s.sample_id] === undefined)
  const elsewhere = rows.filter((s) => {
    const at = picks[s.sample_id]
    return at !== undefined && at !== wellName
  })
  const title = t('batchNew.well.picker', { well: wellName, ref: sampleRef })
  /*
   * 시료 줄 = 표의 한 줄 — 시료 번호(라디오) · 환자 번호 · 타입 · 암종 · 채혈 일시(배정 표와 같은 열 · `wellSampleColumns`) + 끝 칸
   * (「이 웰」 = [빼기] · 「다른 웰에 배정됨」 = 그 웰 이름). eric 2026-10-27 「너비를 더 크게 하고.. 주요한 거 다 보이게 해줘」.
   * 열 폭은 고정이고 긴 값은 말줄임 + 가리키면 전체. 🔴 환자 번호는 PII 가림 그대로 · 빈 값은 대시 가운데
   */
  const columns = wellSampleColumns()
  const cellText = (value: string | undefined, mono: boolean) =>
    value === undefined ? (
      <span className="nbwell__vacant">{t('mask.none')}</span>
    ) : (
      <span className={mono ? 'mono nbwell__text' : 'nbwell__text'} title={value}>
        {value}
      </span>
    )
  const row = (s: Sample, gateFor: ControlGate, tail: ReactNode) => (
    <tr key={s.sample_id}>
      {columns.map((column) => {
        const value = column.value(s, view)
        return (
          <td className={value === undefined ? 'nbwell__vacantcell' : undefined} key={column.key}>
            {column.key === 'sampleId' ? (
              <Radio
                checked={picks[s.sample_id] === wellName}
                label={maskPii(s.sample_id, view)}
                name={`nbwell-${wellName}`}
                onChange={() => onPick(s.sample_id)}
                value={s.sample_id}
                {...gateFor}
              />
            ) : (
              cellText(value, column.mono)
            )}
          </td>
        )
      })}
      <td className="nbwell__tail">{tail}</td>
    </tr>
  )
  const section = (label: string, body: ReactNode, extra?: string) => (
    <tbody
      aria-label={label}
      className={extra === undefined ? 'nbwell__sec' : `nbwell__sec ${extra}`}
    >
      <tr>
        <th className="nbwell__cap" colSpan={columns.length + 1} scope="rowgroup">
          {label}
        </th>
      </tr>
      {body}
    </tbody>
  )

  return (
    <DialogSurface
      aria-label={title}
      aria-labelledby={undefined}
      title={t('batchNew.well.title', { well: wellName, ref: sampleRef })}
      onClose={onClose}
      className="nbwell"
      ref={boxRef}
      role="dialog"
    >
      <SearchInput
        label={t('filter.search.samples')}
        onChange={setQuery}
        placeholder=""
        value={query}
      />
      {patientLock === undefined ? null : (
        <p className="muted nbwell__lock">
          {t('batchNew.well.samePatient', {
            ref: patientLock.sampleRef,
            patient:
              patientLock.patientId === undefined
                ? t('mask.none')
                : maskPii(patientLock.patientId, view),
          })}
        </p>
      )}
      {rows.length === 0 && assigned.length === 0 ? (
        <p className="muted nbwell__empty">
          {patientLock !== undefined && samples.length > 0 && q === ''
            ? t('batchNew.well.samePatientEmpty')
            : emptyLabel}
        </p>
      ) : (
        <div className="nbwell__list">
          <table className="a-tbl nbwell__tbl">
            <caption className="a-sr">{title}</caption>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} scope="col">
                    {column.header}
                  </th>
                ))}
                <th scope="col">
                  <span className="a-sr">{t('column.well')}</span>
                </th>
              </tr>
            </thead>
            {current.length === 0
              ? null
              : section(
                  t('batchNew.well.current'),
                  current.map((s) =>
                    row(
                      s,
                      controlGate,
                      <Button onClick={onClear} size="pill" {...controlGate}>
                        {t('batchNew.well.clear')}
                      </Button>,
                    ),
                  ),
                )}
            {section(
              t('batchNew.well.free'),
              free.length === 0 ? (
                <tr>
                  <td className="muted" colSpan={columns.length + 1}>
                    {t(
                      patientLock === undefined
                        ? 'state.empty.freeSamples'
                        : 'batchNew.well.samePatientEmpty',
                    )}
                  </td>
                </tr>
              ) : (
                free.map((s) => row(s, controlGate, null))
              ),
            )}
            {elsewhere.length === 0
              ? null
              : section(
                  t('batchNew.well.elsewhere'),
                  elsewhere.map((s) => {
                    const at = picks[s.sample_id] ?? ''
                    return row(
                      s,
                      { disabled: true, disabledReason: t('batchNew.well.taken', { well: at }) },
                      <span aria-hidden="true" className="mono">
                        {at}
                      </span>,
                    )
                  }),
                  'nbwell__sec--taken',
                )}
            {assigned.length === 0
              ? null
              : section(
                  t('batchNew.well.assigned'),
                  assigned.map((s) => {
                    const reason = t(
                      rerunBlocked(s)
                        ? 'batchNew.well.processedReason'
                        : 'batchNew.well.assignedReason',
                    )
                    return row(
                      s,
                      { disabled: true, disabledReason: reason },
                      onInspectAssigned === undefined ? null : (
                        <Button
                          size="pill"
                          onClick={() => {
                            if (s.batch_id != null) onInspectAssigned?.(s.batch_id)
                          }}
                        >
                          {t('batchNew.well.inspectAssigned')}
                        </Button>
                      ),
                    )
                  }),
                  'nbwell__sec--taken',
                )}
          </table>
        </div>
      )}
    </DialogSurface>
  )
}

/**
 * 새 시료 등록 — 저장하면 바로 목록에 뜬다(2233행). 페이지(장비 관점 콘솔) 전용 — 표 아래 카드 · 등록만(담기는 목록에서).
 * 🔴 **필수는 `sample_id` 뿐이고 임상 항목은 배치 종료 후 소급 수집이 불가능**해 항상 노출한다
 *    (계약 산문). 빈 칸은 **키 자체를 빼고** 보낸다(`registerSampleVars`).
 */
export function NewSampleForm({
  controlGate,
  error,
  hints,
  onClose,
  onRegister,
  registering,
}: {
  controlGate: ControlGate
  error?: string | undefined
  hints: SampleHints
  onClose: () => void
  onRegister?: (draft: SampleDraft) => void
  registering: boolean
}) {
  const [draft, setDraft] = useState<SampleDraft>({
    sample_id: '',
    patient_id: '',
    sample_type: '',
    rbc_concentration: '',
    blood_draw_date: '',
    cancer_type: '',
    cancer_stage: '',
  })
  const set = (key: keyof SampleDraft) => (value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))
  const ids = useId()

  const saveGate =
    draft.sample_id.trim() === ''
      ? ({ disabled: true, disabledReason: t('reason.sampleIdRequired') } as const)
      : registering
        ? ({ disabled: true, disabledReason: t('reason.requestInFlight') } as const)
        : onRegister === undefined
          ? ({ disabled: true, disabledReason: t('reason.demoSource') } as const)
          : controlGate

  const fields = (
    <>
      <FormRow cols={3}>
        <Field htmlFor={`${ids}-id`} label={t('column.sampleId')} required>
          <TextInput
            id={`${ids}-id`}
            name="sample_id"
            onChange={set('sample_id')}
            value={draft.sample_id}
          />
        </Field>
        <Field htmlFor={`${ids}-patient`} label={t('column.patientId')}>
          <TextInput
            id={`${ids}-patient`}
            name="patient_id"
            onChange={set('patient_id')}
            value={draft.patient_id}
          />
        </Field>
        <Field htmlFor={`${ids}-type`} label={t('column.sampleType')}>
          <SampleHintInput
            id={`${ids}-type`}
            label={t('column.sampleType')}
            name="sample_type"
            onChange={set('sample_type')}
            optionLabel={sampleTypeHintLabel}
            options={hints.sampleType}
            toggleLabel={t('sampleNew.hints.open', { field: t('column.sampleType') })}
            value={draft.sample_type}
          />
        </Field>
      </FormRow>
      {/*
        🔴 적혈구 농도 칸은 없다 — 시료 추가 창과 같은 칸만 받는다(eric 2026-10-04 「metadata 들어갈 거야」 · 2026-10-23
           「왜 적혈구 농도가 있어?」). 초안의 빈 값은 등록 본문에 실리지 않는다(`registerSampleVars` — 숫자가 아니면 뺀다).
      */}
      <FormRow cols={3}>
        <Field label={t('column.drawDate')}>
          <TextInput onChange={set('blood_draw_date')} value={draft.blood_draw_date} />
        </Field>
        <Field htmlFor={`${ids}-cancer`} label={t('column.cancerType')}>
          <SampleHintInput
            id={`${ids}-cancer`}
            label={t('column.cancerType')}
            name="cancer_type"
            onChange={set('cancer_type')}
            options={hints.cancerType}
            toggleLabel={t('sampleNew.hints.open', { field: t('column.cancerType') })}
            value={draft.cancer_type}
          />
        </Field>
        <Field htmlFor={`${ids}-stage`} label={t('column.cancerStage')}>
          <SampleHintInput
            id={`${ids}-stage`}
            label={t('column.cancerStage')}
            name="cancer_stage"
            onChange={set('cancer_stage')}
            options={hints.cancerStage}
            toggleLabel={t('sampleNew.hints.open', { field: t('column.cancerStage') })}
            value={draft.cancer_stage}
          />
        </Field>
      </FormRow>
    </>
  )

  return (
    <div className="card" style={{ marginTop: 'var(--space-lg)' }}>
      <div className="card__title">{t('batchNew.newSample.title')}</div>
      {fields}
      {error === undefined ? null : (
        <p className="deny" role="alert">
          {error}
        </p>
      )}
      <div className="btnrow btnrow--end">
        <Button onClick={onClose} size="pill">
          {t('action.cancel')}
        </Button>
        <Button onClick={() => onRegister?.(draft)} size="pill" variant="primary" {...saveGate}>
          {t('action.addSample')}
        </Button>
      </div>
    </div>
  )
}
